"use client";

import { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PromptInput } from "@/components/PromptInput";
import { SettingsPanel, ImageSettings } from "@/components/SettingsPanel";
import { ImageDisplay } from "@/components/ImageDisplay";
import { HistoryGallery } from "@/components/HistoryGallery";
import { AuthModal } from "@/components/AuthModal";
import { useImageGeneration } from "@/hooks/useImageGeneration";
import { useVideoGeneration } from "@/hooks/useVideoGeneration";
import { useImageEdit } from "@/hooks/useImageEdit";
import { useAuth } from "@/contexts/AuthContext";
import {
  downloadImage,
  downloadVideo,
  getVideoList,
  VideoResponse,
} from "@/lib/api";
import {
  Sparkles,
  Settings,
  History,
  AlertCircle,
  User,
  LogOut,
  LogIn,
  Shield,
  Clock,
  Loader2,
  ImageIcon,
  Video,
  Pencil,
  Key,
} from "lucide-react";
import Link from "next/link";

export default function Home() {
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();

  const {
    isGenerating,
    isUploading,
    error,
    currentImage,
    history,
    generate,
    upload,
    clearError,
    selectFromHistory,
    removeFromHistory,
    refreshHistory,
  } = useImageGeneration();

  const {
    isGenerating: isGeneratingVideo,
    videoStatus,
    error: videoError,
    generate: generateVideo,
    reset: resetVideo,
  } = useVideoGeneration({
    onComplete: () => {
      // Refresh history to update video status on images
      refreshHistory();
      fetchVideos();
    },
    onError: (err) => {
      console.error("Video generation error:", err);
    },
  });

  const {
    isEditing,
    editStatus,
    error: editError,
    edit: editImage,
    reset: resetEdit,
  } = useImageEdit({
    onComplete: () => {
      // Refresh history to show new edited image
      refreshHistory();
    },
    onError: (err) => {
      console.error("Image edit error:", err);
    },
  });

  const [settings, setSettings] = useState<ImageSettings>({
    width: 1024,
    height: 1024,
    seed: null,
    steps: 9,
  });

  const [lastPrompt, setLastPrompt] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [displayPrompt, setDisplayPrompt] = useState("");
  const [videos, setVideos] = useState<VideoResponse[]>([]);

  // Fetch videos list
  const fetchVideos = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await getVideoList();
      setVideos(response.videos);
    } catch (err) {
      console.error("Failed to fetch videos:", err);
    }
  }, [isAuthenticated]);

  // Load videos on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchVideos();
    } else {
      setVideos([]);
    }
  }, [isAuthenticated, fetchVideos]);

  const handleGenerate = useCallback(
    async (prompt: string) => {
      if (!isAuthenticated) {
        setShowAuthModal(true);
        return;
      }

      setLastPrompt(prompt);
      clearError();

      await generate({
        prompt,
        width: settings.width,
        height: settings.height,
        seed: settings.seed,
        num_inference_steps: settings.steps,
      });
    },
    [generate, settings, clearError, isAuthenticated]
  );

  const handleDownload = useCallback(() => {
    if (currentImage) {
      downloadImage(currentImage.image_id, `z-image-${currentImage.image_id}.png`);
    }
  }, [currentImage]);

  const handleGenerateVideo = useCallback(async (prompt: string) => {
    if (!currentImage) return;
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    try {
      await generateVideo(currentImage.image_id, prompt);
    } catch (err) {
      console.error("Failed to start video generation:", err);
    }
  }, [currentImage, generateVideo, isAuthenticated]);

  const handleEditImage = useCallback(async (prompt: string) => {
    if (!currentImage) return;
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    try {
      await editImage(currentImage.image_id, prompt);
    } catch (err) {
      console.error("Failed to start image edit:", err);
    }
  }, [currentImage, editImage, isAuthenticated]);

  const handleDownloadVideo = useCallback(() => {
    const videoId = videoStatus?.video_id || currentImage?.video_id;
    if (videoId) {
      downloadVideo(videoId, `z-video-${videoId}.mp4`);
    }
  }, [videoStatus, currentImage]);

  const handleDeleteVideo = useCallback((videoId: string) => {
    setVideos((prev) => prev.filter((v) => v.video_id !== videoId));
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!isAuthenticated) {
        setShowAuthModal(true);
        return;
      }

      try {
        await upload(file);
      } catch (err) {
        console.error("Upload failed:", err);
      }
    },
    [upload, isAuthenticated]
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">C-Image Generator</h1>
            <span className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded-full">
              Turbo
            </span>

            {/* Generation Status Indicator */}
            {isGenerating && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full animate-pulse">
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
                <ImageIcon className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-primary">Generating...</span>
              </div>
            )}
            {isGeneratingVideo && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-full animate-pulse">
                <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
                <Video className="h-4 w-4 text-purple-500" />
                <span className="text-xs font-medium text-purple-500">Video {videoStatus?.status === "processing" ? `${Math.round((videoStatus?.progress || 0) * 100)}%` : "Queued"}</span>
              </div>
            )}
            {isEditing && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full animate-pulse">
                <Loader2 className="h-4 w-4 text-violet-500 animate-spin" />
                <Pencil className="h-4 w-4 text-violet-500" />
                <span className="text-xs font-medium text-violet-500">Editing {editStatus?.progress ? `${Math.round(editStatus.progress * 100)}%` : "..."}</span>
              </div>
            )}
          </div>

          {/* Auth Section */}
          <div className="flex items-center gap-3">
            {authLoading ? (
              <div className="h-9 w-24 bg-secondary animate-pulse rounded-md" />
            ) : isAuthenticated && user ? (
              <>
                {/* Admin Link (only for admin users) */}
                {user.role === "admin" && (
                  <Link
                    href="/admin"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </Link>
                )}
                {/* API Keys Link (for active users) */}
                {(user.role === "user" || user.role === "admin") && (
                  <Link
                    href="/api-keys"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                  >
                    <Key className="h-4 w-4" />
                    API
                  </Link>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>{user.name}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={logout}
                  className="gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAuthModal(true)}
                className="gap-2"
              >
                <LogIn className="h-4 w-4" />
                Login
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Suspended User Alert */}
        {isAuthenticated && user?.role === "suspended" && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">
                Account Suspended
              </p>
              <p className="text-sm text-destructive/80">
                Your account has been suspended. You can view your existing content but cannot create new images or videos.
              </p>
            </div>
          </div>
        )}

        {/* Login Required Alert */}
        {!isAuthenticated && !authLoading && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary">
                Login Required
              </p>
              <p className="text-sm text-primary/80">
                Please login or create an account to generate and save images.
              </p>
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">
                Generation Failed
              </p>
              <p className="text-sm text-destructive/80">{error}</p>
            </div>
          </div>
        )}

        <div className="grid-layout-main">
          {/* Left Column - Main Content */}
          <div className="space-y-6">
            {/* Prompt Input Card */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Create Image
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PromptInput
                  onGenerate={handleGenerate}
                  isGenerating={isGenerating}
                  initialPrompt={displayPrompt}
                />
              </CardContent>
            </Card>

            {/* Image Display */}
            <Card>
              <CardContent className="pt-6">
                <ImageDisplay
                  image={currentImage}
                  isGenerating={isGenerating}
                  isGeneratingVideo={isGeneratingVideo}
                  videoStatus={videoStatus}
                  isEditing={isEditing}
                  editStatus={editStatus}
                  onGenerateVideo={handleGenerateVideo}
                  onEditImage={handleEditImage}
                  onDownload={handleDownload}
                  onDownloadVideo={handleDownloadVideo}
                />
              </CardContent>
            </Card>

            {/* Settings - Bottom of Left Column */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SettingsPanel
                  settings={settings}
                  onChange={setSettings}
                  disabled={isGenerating}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar - History */}
          <div className="md:sticky md:top-20 md:self-start md:h-[calc(100vh-120px)]">
            <Card className="h-full flex flex-col">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="h-5 w-5" />
                  History
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-hidden flex-1">
                {isAuthenticated ? (
                  <HistoryGallery
                    images={history}
                    videos={videos}
                    onSelect={(image) => {
                      selectFromHistory(image);
                      setDisplayPrompt(image.prompt);
                      resetVideo();
                      resetEdit();
                    }}
                    onSelectVideo={(video) => {
                      // Find the source image and select it
                      const sourceImage = history.find(
                        (img) => img.video_id === video.video_id
                      );
                      if (sourceImage) {
                        selectFromHistory(sourceImage);
                        setDisplayPrompt(sourceImage.prompt);
                      }
                    }}
                    onDelete={removeFromHistory}
                    onDeleteVideo={handleDeleteVideo}
                    onUpload={handleUpload}
                    isUploading={isUploading}
                    selectedId={currentImage?.image_id}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Login to view history</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12 py-6 text-center text-sm text-muted-foreground">
        Powered by Z-Image Turbo (Tongyi-MAI)
      </footer>

      {/* Auth Modal */}
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />

      {/* Pending User Overlay */}
      {isAuthenticated && user && user.role === "pending" && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
              <CardTitle className="text-xl">Pending Approval</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground">
                Your account registration is complete. Please wait for administrator approval before using the service.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">Email:</span>{" "}
                  {user.email}
                </p>
                <p className="mt-1">
                  <span className="font-medium text-foreground">Name:</span>{" "}
                  {user.name}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={logout}
                className="w-full"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
